import { HasProposedValue, PreviousValueOf, ProposedOrPrevious, ProposedValueOf } from "../../../../../ChronoGraph/chrono/Effect.js";
import { Mixin } from "../../../../../ChronoGraph/class/BetterMixin.js";
import { CalculateProposed, VariableInputState } from "../../../../../ChronoGraph/cycle_resolver/CycleResolver.js";
import { Direction, SchedulingMode } from "../../../../scheduling/Types.js";
import { DurationVar, EndDateVar, StartDateVar } from "../../scheduler_basic/BaseEventDispatcher.js";
import { EffortVar, UnitsVar } from "../../scheduler_pro/HasEffortDispatcher.js";
import { HasSchedulingModeMixin } from "../../scheduler_pro/HasSchedulingModeMixin.js";
import { fixedEffortSEDWUBackward, fixedEffortSEDWUForward } from "./FixedEffortDispatcher.js";
//---------------------------------------------------------------------------------------------------------------------
/**
 * This mixin provides the fixed effort scheduling mode facility. The scheduling mode is controlled with the
 * [[HasSchedulingModeMixin.schedulingMode]] field.
 *
 * See [[HasSchedulingModeMixin]] for more details.
 *
 * In this mode, the effort of the task remains "fixed" as the name suggest. It is changed only if there's no other options,
 * for example if both "duration" and "units" has changed. In other cases, some other variable is updated.
 */
export class FixedEffortMixin extends Mixin([HasSchedulingModeMixin], (base) => {
    const superProto = base.prototype;
    class FixedEffortMixin extends base {
        *prepareDispatcher(YIELD) {
            const schedulingMode = yield* this.effectiveSchedulingMode();
            const cycleDispatcher = yield* superProto.prepareDispatcher.call(this, YIELD);
            if (schedulingMode === SchedulingMode.FixedEffort) {
                if (yield HasProposedValue(this.$.assigned))
                    cycleDispatcher.addProposedValueFlag(UnitsVar);
                // If duration to effort normalization is off
                // we expect first assignment to recalculate effort
                // @ts-ignore
                if (!this.constructor.normalizeEffortIfUnassigned) {
                    const previousAssigned = yield PreviousValueOf(this.$.assigned);
                    const proposedEffort = yield ProposedValueOf(this.$.effort);
                    // if that's the first assignment and we have no non-zero effort provided
                    if (!previousAssigned?.size && !proposedEffort) {
                        const input = cycleDispatcher.input.get(EffortVar);
                        // tell the Engine the effort had no previous value
                        // to enforce its recalculation
                        if (input & VariableInputState.HasPreviousValue) {
                            cycleDispatcher.input.set(EffortVar, input ^ VariableInputState.HasPreviousValue);
                        }
                    }
                    // if the event already had assignments of non-zero effort is provided
                    else {
                        // hint the Engine the effort should be preserved
                        cycleDispatcher.addKeepIfPossibleFlag(EffortVar);
                    }
                }
                else {
                    // hint the Engine the effort should be preserved
                    cycleDispatcher.addKeepIfPossibleFlag(EffortVar);
                }
            }
            return cycleDispatcher;
        }
        cycleResolutionContext(Y) {
            const schedulingMode = this.effectiveSchedulingModeSync(Y);
            if (schedulingMode === SchedulingMode.FixedEffort) {
                const direction = Y(this.$.effectiveDirection);
                return direction.direction === Direction.Forward || direction.direction === Direction.None ? fixedEffortSEDWUForward : fixedEffortSEDWUBackward;
            }
            else {
                return superProto.cycleResolutionContext.call(this, Y);
            }
        }
        *calculateEffort() {
            const schedulingMode = yield this.$.schedulingMode;
            const normalizeEffortIfUnassigned = this.constructor.normalizeEffortIfUnassigned;
            // Unlike other effort-aware modes we need FixedEffort to work even when
            // there are no assignments to prevent normalizing duration to effort
            if (!normalizeEffortIfUnassigned && schedulingMode === SchedulingMode.FixedEffort) {
                const assignmentsByCalendar = yield this.$.assignmentsByCalendar;
                // use proposed value as is when no assignments
                if (!assignmentsByCalendar.size) {
                    return yield* this.calculateEffortProposed();
                }
            }
            return yield* superProto.calculateEffort.call(this);
        }
        *calculateStartDate() {
            const dispatch = yield this.$.dispatcher;
            const preserveSEDFieldsIfManual = this.constructor.preserveSEDFieldsIfManual;
            const schedulingMode = yield* this.effectiveSchedulingMode();
            // If we should keep start/end/duration intact for manual FixedEffort tasks
            if (preserveSEDFieldsIfManual && schedulingMode === SchedulingMode.FixedEffort) {
                const manuallyScheduled = yield this.$.manuallyScheduled;
                // If the task is manually scheduled
                if (manuallyScheduled) {
                    const formulaId = dispatch.resolution.get(StartDateVar);
                    // If startDate is decided to recalculate
                    if (formulaId !== CalculateProposed &&
                        // but none of start/end/duration has been changed
                        !((yield HasProposedValue(this.$.startDate)) ||
                            (yield HasProposedValue(this.$.endDate)) ||
                            (yield HasProposedValue(this.$.duration)))) {
                        // stick to previous value as is
                        return yield ProposedOrPrevious;
                    }
                }
            }
            return yield* super.calculateStartDate();
        }
        *calculateEndDate() {
            const dispatch = yield this.$.dispatcher;
            const preserveSEDFieldsIfManual = this.constructor.preserveSEDFieldsIfManual;
            const schedulingMode = yield* this.effectiveSchedulingMode();
            // If we should keep start/end/duration intact for manual FixedEffort tasks
            if (preserveSEDFieldsIfManual && schedulingMode === SchedulingMode.FixedEffort) {
                const manuallyScheduled = yield this.$.manuallyScheduled;
                // If the task is manually scheduled
                if (manuallyScheduled) {
                    const formulaId = dispatch.resolution.get(EndDateVar);
                    // If endDate is decided to recalculate
                    if (formulaId !== CalculateProposed &&
                        // but none of start/end/duration has been changed
                        !((yield HasProposedValue(this.$.startDate)) ||
                            (yield HasProposedValue(this.$.endDate)) ||
                            (yield HasProposedValue(this.$.duration)))) {
                        return yield ProposedOrPrevious;
                    }
                }
            }
            return yield* super.calculateEndDate();
        }
        *calculateDuration() {
            const dispatch = yield this.$.dispatcher;
            const preserveSEDFieldsIfManual = this.constructor.preserveSEDFieldsIfManual;
            const schedulingMode = yield* this.effectiveSchedulingMode();
            // If we should keep start/end/duration intact for manual FixedEffort tasks
            if (preserveSEDFieldsIfManual && schedulingMode === SchedulingMode.FixedEffort) {
                const manuallyScheduled = yield this.$.manuallyScheduled;
                // If the task is manually scheduled
                if (manuallyScheduled) {
                    const formulaId = dispatch.resolution.get(DurationVar);
                    // If duration is decided to recalculate
                    if (formulaId !== CalculateProposed &&
                        // but none of start/end/duration has been changed
                        !((yield HasProposedValue(this.$.startDate)) ||
                            (yield HasProposedValue(this.$.endDate)) ||
                            (yield HasProposedValue(this.$.duration)))) {
                        return yield ProposedOrPrevious;
                    }
                }
            }
            return yield* super.calculateDuration();
        }
        *calculateAssignmentUnitsProposed(assignment) {
            const preserveSEDFieldsIfManual = this.constructor.preserveSEDFieldsIfManual;
            const schedulingMode = yield* this.effectiveSchedulingMode();
            // If we should keep start/end/duration intact for manual FixedEffort tasks
            if (preserveSEDFieldsIfManual && schedulingMode === SchedulingMode.FixedEffort) {
                const manuallyScheduled = yield this.$.manuallyScheduled;
                // If the task is manually scheduled
                if (manuallyScheduled) {
                    return yield* super.calculateAssignmentUnitsPure(assignment);
                }
            }
            return yield* super.calculateAssignmentUnitsProposed(assignment);
        }
    }
    FixedEffortMixin.normalizeEffortIfUnassigned = true;
    /**
     * Set this to `true` to "fix" start/end/duration values (similar to `FixedDuration` mode)
     * for manually scheduled tasks
     * @static
     */
    FixedEffortMixin.preserveSEDFieldsIfManual = false;
    return FixedEffortMixin;
}) {
}
